import React, { useEffect, useState } from 'react';
import { Badge, Button, Drawer, Spin, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { ancanaApi } from '../../../api/ancanaApi';
import { ISeoMeta } from '../../../types/models/seo';
import SeoPanel from './SeoPanel';
import SeoScoreBadge from './SeoScoreBadge';

/**
 * SEO column for the listings / destinations / posts tables: the current score, and a
 * button opening the editor in a side drawer.
 *
 * The drawer is the point. It puts SEO next to the content it describes without adding
 * another tab to already-dense edit forms, and it means a whole table can be triaged in
 * one pass — which is what the score column is for.
 */

// One request per table page instead of one per row.
const seoMetaCache = new Map<string, ISeoMeta | null>();

export interface SeoStatusCellProps {
  contentTypeId?: number;
  objectId?: number | string;
  /** Public path of the page, for the previews. */
  path?: string;
  fallbackImageUrl?: string | null;
  galleryId?: number;
  /** Shown in the drawer title so it's clear which record is being edited. */
  recordLabel?: string;
}

const SeoStatusCell = ({
  contentTypeId,
  objectId,
  path,
  fallbackImageUrl,
  galleryId,
  recordLabel,
}: SeoStatusCellProps) => {
  const [seoMeta, setSeoMeta] = useState<ISeoMeta | null | undefined>();
  const [isOpen, setIsOpen] = useState(false);
  const cacheKey = `${contentTypeId}:${objectId}`;

  const load = async (force = false) => {
    if (!contentTypeId || objectId === undefined) return;
    if (!force && seoMetaCache.has(cacheKey)) {
      setSeoMeta(seoMetaCache.get(cacheKey));
      return;
    }
    try {
      const response = await ancanaApi.get(
        `seo/meta/?content_type=${contentTypeId}&object_id=${objectId}`,
      );
      const row: ISeoMeta | null = (response.data?.results || [])[0] || null;
      seoMetaCache.set(cacheKey, row);
      setSeoMeta(row);
    } catch {
      setSeoMeta(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  if (seoMeta === undefined) {
    return <Spin size="small" />;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {seoMeta ? (
        <SeoScoreBadge seoMeta={seoMeta} />
      ) : (
        <Tooltip title="No overrides — the page derives its metadata from its own content.">
          <Badge status="default" text="Derived" />
        </Tooltip>
      )}
      <Button size="small" type="text" icon={<SearchOutlined />} onClick={() => setIsOpen(true)}>
        SEO
      </Button>

      <Drawer
        title={`SEO — ${recordLabel || ''}`}
        placement="right"
        width="80%"
        visible={isOpen}
        onClose={() => setIsOpen(false)}
        destroyOnClose
      >
        <SeoPanel
          contentTypeId={contentTypeId}
          objectId={objectId}
          path={path}
          fallbackImageUrl={fallbackImageUrl}
          galleryId={galleryId}
          onSaved={() => load(true)}
        />
      </Drawer>
    </div>
  );
};

export default SeoStatusCell;
